import { Injectable } from '@angular/core';
import { ConsolidatedError, ExcelRow, ValidationError } from '../doqfy-components/bulk-upload-pdf-estamp/bulk-upload.interfaces';
import { EsignValidatorService } from './esign-validator.service';

@Injectable({ providedIn: 'root' })
export class BothValidatorService {

  readonly requiredColumns: string[] = [
    'referance_id',
    'branch_code',
    'stamp_type',
    'first_party_name',
    'second_party_name',
    'stamp_duty_paid_by',
    'stamp_duty_amount',
    'description',
    'article_code',
    'consideration_amount',
    'execution_date',
    'name',
    'email',
    'contact_number',
    'sign_position',
    'method',
    'pages',
    'user_type',
    'file_name',
  ];

  constructor(private esignValidator: EsignValidatorService) {}

  validate(
    rows: ExcelRow[],
    userBranches: string[],
    refIdFileNameValidation: boolean = false
  ): BothValidationResult {

    const stampErrors: ValidationError[]        = [];
    const fileNameDuplicates: ValidationError[] = [];
    const signPositionErrors: ValidationError[] = [];
    const rowsWithIssuesSet = new Set<ExcelRow>();

    // ── Build reference groups ──────────────────────────────────────────
    // Group all rows by referance_id. After _processRawRows inheritance,
    // every row has referance_id. The FIRST row of each group carries stamp fields.
    const refGroups = new Map<string, { row: ExcelRow; index: number }[]>();
    rows.forEach((row, index) => {
      const refId = row.referance_id?.toString().trim().toLowerCase() ?? '';
      if (!refId) return;
      if (!refGroups.has(refId)) refGroups.set(refId, []);
      refGroups.get(refId)!.push({ row, index });
    });

    // 1. Stamp field validation — only on the FIRST row of each group.
    //    Signatory-only rows (rows 2..N of a group) intentionally have no stamp fields.
    refGroups.forEach((entries) => {
      const { row, index } = entries[0]; // first row only
      const errors: string[] = [];

      const stampType = row.stamp_type?.toString().trim().toLowerCase();
      if (!stampType) {
        errors.push('stamp_type is required.');
      }

      if (!row.first_party_name) {
        errors.push('first_party_name is required.');
      } else if (stampType !== 'traditional') {
        if (row.first_party_name.length > 50)
          errors.push('First Party Name should not exceed 50 characters.');
        if (!/^[a-zA-Z0-9\s]+$/.test(row.first_party_name))
          errors.push('First Party Name should not contain special characters.');
      }

      if (!row.second_party_name) {
        errors.push('second_party_name is required.');
      } else if (stampType !== 'traditional') {
        if (row.second_party_name.length > 50)
          errors.push('Second Party Name should not exceed 50 characters.');
        if (!/^[a-zA-Z0-9\s.\/&()]+$/.test(row.second_party_name))
          errors.push('Second Party Name should not contain special characters.');
      }

      if (!row.stamp_duty_paid_by) errors.push('stamp_duty_paid_by is required.');
      if (!row.article_code)       errors.push('article_code is required.');
      if (!row.description)        errors.push('description is required.');
      if (!row.execution_date)     errors.push('execution_date is required.');

      const amt = Number(row.stamp_duty_amount);
      if (row.stamp_duty_amount === undefined || row.stamp_duty_amount === null || isNaN(amt)) {
        errors.push('stamp_duty_amount must be a valid number.');
      }

      if (errors.length > 0) {
        stampErrors.push({ rowNumber: index + 2, error: errors.join(' ') });
        rowsWithIssuesSet.add(row);
      }
    });

    // 2. Same file_name must NOT appear under a different reference_id.
    const fileToRefId = new Map<string, string>();
    rows.forEach((row, index) => {
      const fileName = row.file_name?.toString().trim().toLowerCase() ?? '';
      const refId    = row.referance_id?.toString().trim().toLowerCase() ?? '';
      if (!fileName || !refId) return;

      if (fileToRefId.has(fileName)) {
        if (fileToRefId.get(fileName) !== refId) {
          const errMsg = `File "${row.file_name}" is already used under a different reference ID. Each file must belong to only one reference ID.`;
          fileNameDuplicates.push({ rowNumber: index + 2, error: errMsg });
          rowsWithIssuesSet.add(row);
        }
      } else {
        fileToRefId.set(fileName, refId);
      }
    });

    // 3. Duplicate sign_position within the same PDF file.
    // Group by file_name so the same sign position in different PDF files is allowed.
    const fileGroups = new Map<string, { row: ExcelRow; index: number }[]>();
    rows.forEach((row, index) => {
      const fileName = row.file_name?.toString().trim().toLowerCase() ?? '';
      if (!fileName) return;
      if (!fileGroups.has(fileName)) fileGroups.set(fileName, []);
      fileGroups.get(fileName)!.push({ row, index });
    });

    fileGroups.forEach((entries) => {
      const seenPositions = new Map<string, number>();
      entries.forEach(({ row, index }) => {
        const pos = row.sign_position?.toString().trim().toUpperCase() ?? '';
        if (!pos) return;
        if (seenPositions.has(pos)) {
          const firstRow = seenPositions.get(pos)!;
          const errMsg   = `Duplicate sign_position "${pos}" for file "${row.file_name}" — already used at row ${firstRow}. Each signatory must have a unique sign position within the same file.`;
          signPositionErrors.push({ rowNumber: index + 2, error: errMsg });
          rowsWithIssuesSet.add(row);
        } else {
          seenPositions.set(pos, index + 2);
        }
      });
    });

    // 4. Run eSign field-level validations on ALL rows (name, email, sign_position format etc.)
    //    We reuse esignValidator but exclude its file_name cross-ref and sign_position-duplicate
    //    checks since we handle those above. We capture only field-level errors here.
    const esignResult = this.esignValidator.validate(rows, userBranches, refIdFileNameValidation);

    // 5. Consolidate all errors — merge stamp + fileNameDuplicates + signPosition + esign field errors
    //    De-duplicate by display text.
    const seen = new Set<string>();
    const consolidatedErrors: ConsolidatedError[] = [];

    const allRaw: ValidationError[] = [
      ...stampErrors,
      ...fileNameDuplicates,
      ...signPositionErrors,
    ];

    allRaw.forEach(e => {
      const text = `Row ${e.rowNumber} : ${e.error}`;
      if (!seen.has(text)) {
        seen.add(text);
        consolidatedErrors.push({ displayText: text, originalItem: e });
      }
    });

    // Add esign field-level errors but SKIP any that are about file_name cross-ref
    // or sign_position duplicates (already handled above) to avoid double-reporting.
    esignResult.consolidatedErrors.forEach(e => {
      const isFileNameCrossRef  = e.displayText.includes('already used under a different reference ID');
      const isSignPosDuplicate  = e.displayText.includes('Duplicate sign_position');
      if (!isFileNameCrossRef && !isSignPosDuplicate && !seen.has(e.displayText)) {
        seen.add(e.displayText);
        consolidatedErrors.push(e);
      }
    });

    // Merge rowsWithIssues
    esignResult.rowsWithIssues.forEach(r => rowsWithIssuesSet.add(r));

    // Merge fileNameDuplicateErrors
    const seenFn = new Set<string>();
    const mergedFnDuplicates: ValidationError[] = [];
    [...fileNameDuplicates, ...esignResult.fileNameDuplicateErrors].forEach(e => {
      const key = `${e.rowNumber}-${e.error}`;
      if (!seenFn.has(key)) { seenFn.add(key); mergedFnDuplicates.push(e); }
    });

    // 6. Valid row count
    const allErrorRows = new Set<number>(
      consolidatedErrors
        .map(e => e.originalItem?.rowNumber)
        .filter((n): n is number => n !== undefined)
    );
    const validRowCount = rows.filter((_, i) => !allErrorRows.has(i + 2)).length;

    return {
      isValid: consolidatedErrors.length === 0,
      consolidatedErrors,
      rowsWithIssues: Array.from(rowsWithIssuesSet),
      validRowCount,
      fileNameDuplicateErrors: mergedFnDuplicates,
    };
  }
}

export interface BothValidationResult {
  isValid: boolean;
  consolidatedErrors: ConsolidatedError[];
  rowsWithIssues: ExcelRow[];
  validRowCount: number;
  fileNameDuplicateErrors: ValidationError[];
}